import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  ArrowUUpLeft,
  Bell,
  CheckCircle,
  ClockClockwise,
  CreditCard,
  DownloadSimple,
  Eye,
  Gauge,
  Heartbeat,
  Key,
  Lightning,
  Lock,
  MagnifyingGlass,
  ClipboardText,
  EnvelopeSimple,
  Skull,
  Trash,
  WarningOctagon,
  Wrench,
  X,
} from "@phosphor-icons/react";
import { Admin, AdminToken } from "../lib/api";
import { AdminAudit, HealthDeep } from "../lib/auth";
import BillingOpsPanel from "../components/admin/BillingOpsPanel";
import ContactInboxPanel from "../components/admin/ContactInboxPanel";

const STATUS_CHIP = {
  delivered: { cls: "chip", label: "OK" },
  pending: { cls: "chip", label: "PENDING" },
  pending_retry: { cls: "chip", label: "RETRY" },
  in_flight: { cls: "chip chip-blue", label: "IN FLIGHT" },
  failed: { cls: "chip chip-live", label: "FAILED" },
  dead: { cls: "chip chip-live", label: "DEAD" },
};

const STATUS_FILTERS = ["dead", "failed", "pending_retry", "delivered", "all"];

const fmtAge = (secs) => {
  if (secs == null) return "—";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
};

const fmtIso = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return s;
  }
};

const fmtPct = (r) => (r == null ? "—" : `${(r * 100).toFixed(1)}%`);
const fmtMs = (m) => (m == null ? "—" : `${m.toFixed(0)}ms`);

const VALID_TABS = ["deliveries", "slo", "alerts", "maintenance", "billing", "secrets", "audit", "contact", "health"];

const _initialTab = () => {
  if (typeof window === "undefined") return "deliveries";
  const params = new URLSearchParams(window.location.search);
  const t = params.get("tab");
  return VALID_TABS.includes(t) ? t : "deliveries";
};

export default function AdminPage() {
  const [token, setToken] = useState(AdminToken.get());
  const [tab, setTab] = useState(_initialTab);

  // Keep the URL in sync with the active tab so deep-links like
  // /admin?tab=billing land directly on the right panel and operators
  // can bookmark/share specific tools.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("tab") !== tab) {
      url.searchParams.set("tab", tab);
      window.history.replaceState({}, "", url.toString());
    }
  }, [tab]);

  // Validate token & gate the page
  if (!token) {
    return <TokenGate onSet={(t) => { AdminToken.set(t); setToken(t); }} />;
  }
  return (
    <div className="px-4 py-6 space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between border-b-2 border-black pb-3">
        <div>
          <h1 className="font-display font-black tracking-tighter text-3xl">OPS COCKPIT</h1>
          <div className="label-mono text-neutral-500 text-xs">
            ADMIN TOKEN ACTIVE · READ-ONLY DIAGNOSTICS + CONTROLLED REPLAY
          </div>
        </div>
        <button
          className="btn-flat-outline text-xs"
          data-testid="admin-logout-btn"
          onClick={() => { AdminToken.set(""); setToken(""); }}
        >
          <Lock size={14} weight="bold" /> SIGN OUT
        </button>
      </div>

      <div className="flex gap-2 border-b-2 border-black overflow-x-auto">
        {[
          ["deliveries", "DEAD-LETTER", Skull],
          ["slo", "PARTNER SLO", Gauge],
          ["alerts", "ALERTS", Bell],
          ["maintenance", "MAINTENANCE", Wrench],
          ["billing", "BILLING", CreditCard],
          ["secrets", "SECRETS", Key],
          ["audit", "AUDIT", ClipboardText],
          ["contact", "INBOX", EnvelopeSimple],
          ["health", "HEALTH", Heartbeat],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 border-2 border-b-0 border-black -mb-[2px] font-display tracking-wider text-sm whitespace-nowrap ${
              tab === k ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
            }`}
          >
            <Icon size={14} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {tab === "deliveries" && <DeliveriesPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "slo" && <SloPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "alerts" && <AlertsPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "maintenance" && <MaintenancePanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "billing" && <BillingOpsPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "secrets" && <SecretsPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "audit" && <AuditLogPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "contact" && <ContactInboxPanel onAuthFail={() => { AdminToken.set(""); setToken(""); }} />}
      {tab === "health" && <HealthPanel />}
    </div>
  );
}

// =============== TOKEN GATE ===============

function TokenGate({ onSet }) {
  const [val, setVal] = useState("");
  return (
    <div className="px-4 py-12" data-testid="admin-token-gate">
      <div className="max-w-md mx-auto border-2 border-black p-6 space-y-4 bg-white">
        <div className="flex items-center gap-2">
          <Lock size={20} weight="bold" />
          <h1 className="font-display font-black tracking-tighter text-2xl">OPS GATE</h1>
        </div>
        <p className="text-sm">
          Enter the operator <code className="font-mono bg-neutral-100 px-1">X-Admin-Token</code> to access the
          dead-letter inspector, partner SLO dashboard, and controlled replay tooling.
        </p>
        <input
          data-testid="admin-token-input"
          autoFocus
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="admin token"
          className="w-full border-2 border-black px-3 py-2 font-mono"
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) onSet(val.trim()); }}
        />
        <button
          data-testid="admin-token-submit"
          disabled={!val.trim()}
          onClick={() => onSet(val.trim())}
          className="btn-flat w-full disabled:opacity-50"
        >
          UNLOCK
        </button>
        <div className="label-mono text-xs text-neutral-500">
          Token is stored in this browser only (localStorage). Sign out clears it.
        </div>
      </div>
    </div>
  );
}

// =============== DEAD-LETTER INSPECTION ===============

function DeliveriesPanel({ onAuthFail }) {
  const [status, setStatus] = useState("dead");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // delivery detail
  const [replayingId, setReplayingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await Admin.listDeliveries({ status, limit: 100 });
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("UNAUTHORIZED"); onAuthFail(); return; }
      toast.error("LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const replay = async (id) => {
    if (!window.confirm(`Replay delivery ${id.slice(0, 8)}…?\nThis preserves the original event envelope and creates a new attempt.`)) return;
    setReplayingId(id);
    try {
      const r = await Admin.replayDelivery(id);
      toast.success(`REPLAYED (was ${r.prev_status})`);
      // Wait a beat for the new attempt to settle, then refresh.
      setTimeout(load, 600);
    } catch (e) {
      if (e?.response?.status === 409) toast.error("CANNOT REPLAY: IN FLIGHT");
      else if (e?.response?.status === 404) toast.error("DELIVERY NOT FOUND");
      else toast.error(e?.response?.data?.detail || "REPLAY FAILED");
    } finally {
      setReplayingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-deliveries-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`chip cursor-pointer ${status === s ? "bg-black text-white" : ""}`}
            >
              {s.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu status={status} />
          <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-deliveries-refresh">
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </div>
      </div>

      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-1">STATUS</div>
          <div className="col-span-3">EVENT</div>
          <div className="col-span-4">URL</div>
          <div className="col-span-1 text-center">ATT</div>
          <div className="col-span-1 text-center">HTTP</div>
          <div className="col-span-1 text-center">AGE</div>
          <div className="col-span-1 text-right">ACTIONS</div>
        </div>
        {loading && (
          <div className="px-3 py-6 text-center label-mono text-neutral-500">
            LOADING…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-3 py-8 text-center label-mono text-neutral-500" data-testid="admin-deliveries-empty">
            {status === "dead" ? "NO DEAD DELIVERIES. SYSTEM HEALTHY." : "NO DELIVERIES MATCH FILTER."}
          </div>
        )}
        {items.map((d) => {
          const chip = STATUS_CHIP[d.status] || STATUS_CHIP.pending;
          const replayable = ["failed", "dead", "pending_retry"].includes(d.status);
          return (
            <div
              key={d.id}
              data-testid={`admin-delivery-row-${d.id}`}
              className="grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 hover:bg-neutral-50 text-sm"
            >
              <div className="col-span-1"><span className={chip.cls}>{chip.label}</span></div>
              <div className="col-span-3 font-mono truncate" title={d.event}>{d.event}</div>
              <div className="col-span-4 font-mono text-xs truncate" title={d.url}>{d.url}</div>
              <div className="col-span-1 text-center font-mono">{d.attempts}</div>
              <div className="col-span-1 text-center font-mono">{d.last_status ?? "—"}</div>
              <div className="col-span-1 text-center font-mono">{fmtAge(d.age_seconds)}</div>
              <div className="col-span-1 flex justify-end gap-1">
                <button
                  className="btn-flat-outline px-2 py-1 text-xs"
                  data-testid={`admin-inspect-${d.id}`}
                  onClick={() => setSelected(d.id)}
                  title="Inspect"
                >
                  <Eye size={12} weight="bold" />
                </button>
                {replayable && (
                  <button
                    className="btn-flat-outline px-2 py-1 text-xs"
                    data-testid={`admin-replay-${d.id}`}
                    disabled={replayingId === d.id}
                    onClick={() => replay(d.id)}
                    title="Force replay (admin)"
                  >
                    <ArrowUUpLeft size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <DeliveryDetailModal
          id={selected}
          onClose={() => setSelected(null)}
          onReplay={(id) => { setSelected(null); replay(id); }}
        />
      )}
    </div>
  );
}

function DeliveryDetailModal({ id, onClose, onReplay }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Admin.inspectDelivery(id).then(setData).catch((e) => {
      setErr(e?.response?.data?.detail || "Failed to load");
    });
  }, [id]);

  const d = data?.delivery;
  const env = d?.envelope || {};
  const replayable = d && ["failed", "dead", "pending_retry"].includes(d.status);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="admin-delivery-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-black max-w-3xl w-full max-h-[90vh] overflow-auto modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black sticky top-0 bg-white">
          <div className="font-display font-black tracking-tighter text-xl">DELIVERY INSPECT</div>
          <button className="btn-flat-outline px-2 py-1 text-xs" data-testid="admin-detail-close" onClick={onClose}>
            <X size={14} weight="bold" />
          </button>
        </div>
        {err && <div className="p-4 text-red-600 font-mono text-sm">{err}</div>}
        {!data && !err && <div className="p-4 label-mono text-neutral-500">LOADING…</div>}
        {data && (
          <div className="p-4 space-y-4">
            <Row k="ID" v={d.id} mono />
            <Row k="STATUS" v={d.status} />
            <Row k="EVENT" v={d.event} mono />
            <Row k="EVENT_ID (immutable)" v={env.event_id} mono />
            <Row k="TS (epoch)" v={String(env.ts)} mono />
            <Row k="URL" v={d.url} mono />
            <Row k="ATTEMPTS" v={String(d.attempts || 0)} mono />
            <Row k="LAST HTTP" v={d.last_status ?? "—"} mono />
            <Row k="LAST ERROR" v={d.last_error || "—"} mono wrap />
            <div className="grid grid-cols-2 gap-3 text-xs font-mono">
              <Row k="EMITTED"   v={fmtIso(d.emitted_at)} />
              <Row k="LEASED"    v={fmtIso(d.leased_at)} />
              <Row k="ATTEMPTED" v={fmtIso(d.attempted_at)} />
              <Row k="RESPONDED" v={fmtIso(d.responded_at)} />
              <Row k="NEXT TRY"  v={fmtIso(d.next_attempt_at)} />
              <Row k="REPLAYED"  v={fmtIso(d.replayed_at)} />
            </div>
            {d.replayed_by && (
              <Row k="REPLAYED BY" v={d.replayed_by} mono />
            )}
            {d.last_response_body && (
              <div className="space-y-1">
                <div className="label-mono text-xs">RESPONSE EXCERPT (≤1KB)</div>
                <pre
                  data-testid="admin-detail-response-body"
                  className="border-2 border-black p-2 bg-neutral-50 text-xs overflow-auto max-h-48 whitespace-pre-wrap"
                >{d.last_response_body}</pre>
              </div>
            )}
            <div className="space-y-1">
              <div className="label-mono text-xs">ENVELOPE (immutable, signed payload)</div>
              <pre
                data-testid="admin-detail-envelope"
                className="border-2 border-black p-2 bg-neutral-50 text-xs overflow-auto max-h-64 whitespace-pre-wrap"
              >{JSON.stringify(env, null, 2)}</pre>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t-2 border-black">
              {replayable && (
                <button
                  className="btn-flat"
                  data-testid="admin-detail-replay-btn"
                  onClick={() => onReplay(d.id)}
                >
                  <ArrowUUpLeft size={14} weight="bold" /> FORCE REPLAY
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, mono, wrap }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="label-mono text-neutral-500">{k}</div>
      <div className={`col-span-2 ${mono ? "font-mono" : ""} ${wrap ? "break-all" : "truncate"}`}>{v ?? "—"}</div>
    </div>
  );
}

// =============== PARTNER SLO ===============

function SloPanel({ onAuthFail }) {
  const [windowH, setWindowH] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillId, setDrillId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await Admin.sloPartners(windowH);
      setData(r);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("UNAUTHORIZED"); onAuthFail(); return; }
      toast.error("LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowH]);

  const summary = useMemo(() => {
    if (!data?.partners) return null;
    return {
      total: data.partners.length,
      degraded: data.partners.filter((p) => p.degraded).length,
      silent: data.partners.filter((p) => p.total === 0).length,
    };
  }, [data]);

  return (
    <div className="space-y-4" data-testid="admin-slo-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="label-mono text-xs">WINDOW</span>
          {[1, 6, 24, 168].map((h) => (
            <button
              key={h}
              data-testid={`admin-slo-window-${h}`}
              onClick={() => setWindowH(h)}
              className={`chip cursor-pointer ${windowH === h ? "bg-black text-white" : ""}`}
            >
              {h}H
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-slo-refresh">
          <ArrowsClockwise size={14} weight="bold" /> REFRESH
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-2" data-testid="admin-slo-summary">
          <Stat label="PARTNERS" value={summary.total} />
          <Stat label="DEGRADED" value={summary.degraded} danger={summary.degraded > 0} />
          <Stat label="SILENT" value={summary.silent} />
        </div>
      )}

      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-4">URL</div>
          <div className="col-span-1 text-center">OK</div>
          <div className="col-span-1 text-center">FAIL</div>
          <div className="col-span-1 text-center">DEAD</div>
          <div className="col-span-1 text-center">RETRY</div>
          <div className="col-span-1 text-center">SUCCESS</div>
          <div className="col-span-1 text-center">P50</div>
          <div className="col-span-1 text-center">P95</div>
          <div className="col-span-1 text-center">STATE</div>
        </div>
        {loading && (
          <div className="px-3 py-6 text-center label-mono text-neutral-500">LOADING…</div>
        )}
        {!loading && data?.partners?.length === 0 && (
          <div className="px-3 py-8 text-center label-mono text-neutral-500" data-testid="admin-slo-empty">
            NO WEBHOOKS REGISTERED.
          </div>
        )}
        {data?.partners?.map((p) => (
          <button
            type="button"
            key={p.webhook_id}
            data-testid={`admin-slo-row-${p.webhook_id}`}
            onClick={() => setDrillId(p.webhook_id)}
            title="Drill into per-event breakdown for this partner"
            className={`w-full text-left grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 text-sm hover:bg-neutral-50 ${
              p.degraded ? "bg-red-50 hover:bg-red-100" : ""
            }`}
          >
            <div className="col-span-4 font-mono text-xs truncate flex items-center gap-1" title={p.url}>
              <MagnifyingGlass size={10} weight="bold" className="text-neutral-400" />
              {p.url}
            </div>
            <div className="col-span-1 text-center font-mono">{p.delivered_count}</div>
            <div className="col-span-1 text-center font-mono">{p.failed_count}</div>
            <div className="col-span-1 text-center font-mono">{p.dead_count}</div>
            <div className="col-span-1 text-center font-mono">{p.pending_retry_count}</div>
            <div className="col-span-1 text-center font-mono">{fmtPct(p.success_rate)}</div>
            <div className="col-span-1 text-center font-mono">{fmtMs(p.p50_latency_ms)}</div>
            <div className="col-span-1 text-center font-mono">{fmtMs(p.p95_latency_ms)}</div>
            <div className="col-span-1 text-center">
              {p.degraded ? (
                <span className="chip chip-live inline-flex items-center gap-1">
                  <WarningOctagon size={10} weight="bold" /> DEG
                </span>
              ) : p.total === 0 ? (
                <span className="chip">SILENT</span>
              ) : (
                <span className="chip inline-flex items-center gap-1">
                  <CheckCircle size={10} weight="bold" /> OK
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      {drillId && (
        <PartnerDrillModal
          webhookId={drillId}
          windowH={windowH}
          onClose={() => setDrillId(null)}
        />
      )}
      {data && (
        <div className="label-mono text-xs text-neutral-500" data-testid="admin-slo-footer">
          DEGRADED THRESHOLD: SUCCESS RATE &lt; {(data.degraded_threshold * 100).toFixed(0)}% AND TOTAL ≥ 5 ·
          GENERATED {fmtIso(data.generated_at)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div
      className={`border-2 border-black p-3 ${danger ? "bg-red-50" : "bg-white"}`}
      data-testid={`admin-stat-${label.toLowerCase()}`}
    >
      <div className="label-mono text-xs text-neutral-500">{label}</div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
    </div>
  );
}

// =============== SECRETS WHENCE ===============

function SecretsPanel({ onAuthFail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await Admin.secretsWhence();
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("UNAUTHORIZED"); onAuthFail(); return; }
      toast.error("LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-secrets-panel">
      <div className="flex items-center justify-between">
        <div className="label-mono text-xs text-neutral-500">
          PROVENANCE ONLY · NO VALUES · LEAF-MODULE ENFORCED
        </div>
        <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-secrets-refresh">
          <ArrowsClockwise size={14} weight="bold" /> REFRESH
        </button>
      </div>
      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-3">NAME</div>
          <div className="col-span-2">SOURCE</div>
          <div className="col-span-3">ENV VAR</div>
          <div className="col-span-1 text-center">ENV?</div>
          <div className="col-span-1 text-center">ACTIVE</div>
          <div className="col-span-2">LOADED AT</div>
        </div>
        {loading && <div className="px-3 py-6 label-mono text-neutral-500 text-center">LOADING…</div>}
        {!loading && items.length === 0 && (
          <div className="px-3 py-6 label-mono text-neutral-500 text-center">NO SECRETS BOOTSTRAPPED.</div>
        )}
        {items.map((s) => (
          <div
            key={s.name}
            data-testid={`admin-secret-row-${s.name}`}
            className="grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 text-sm"
          >
            <div className="col-span-3 font-mono">{s.name}</div>
            <div className="col-span-2"><span className="chip">{s.source?.toUpperCase()}</span></div>
            <div className="col-span-3 font-mono text-xs truncate">{s.env_var}</div>
            <div className="col-span-1 text-center font-mono">{s.env_present ? "YES" : "no"}</div>
            <div className="col-span-1 text-center font-mono">{s.active ? "YES" : "no"}</div>
            <div className="col-span-2 font-mono text-xs">{fmtIso(s.loaded_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// =============== EXPORT MENU (Tier 1) ===============

function ExportMenu({ status }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const doExport = async (format) => {
    setBusy(true); setOpen(false);
    try {
      const { blob, filename } = await Admin.exportDeliveries({
        status, format, limit: 10000,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`EXPORTED ${filename}`);
    } catch (e) {
      toast.error(String(e?.message || e).slice(0, 200));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid="admin-export-menu">
      <button
        className="btn-flat-outline text-xs"
        data-testid="admin-export-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
      >
        <DownloadSimple size={14} weight="bold" />
        {busy ? "EXPORTING…" : "EXPORT"}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-20 border-2 border-black bg-white min-w-[180px]"
          data-testid="admin-export-options"
        >
          <button
            className="w-full text-left px-3 py-2 hover:bg-neutral-100 label-mono text-xs"
            data-testid="admin-export-csv"
            onClick={() => doExport("csv")}
          >
            CSV (flat, no envelope)
          </button>
          <button
            className="w-full text-left px-3 py-2 hover:bg-neutral-100 label-mono text-xs border-t border-neutral-300"
            data-testid="admin-export-json"
            onClick={() => doExport("json")}
          >
            JSON (incl. envelope)
          </button>
        </div>
      )}
    </div>
  );
}

// =============== PARTNER DRILL-DOWN MODAL (Tier 1) ===============

function PartnerDrillModal({ webhookId, windowH, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Admin.sloPartnerDrilldown(webhookId, windowH)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [webhookId, windowH]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="admin-partner-drill-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-black max-w-4xl w-full max-h-[90vh] overflow-auto modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black sticky top-0 bg-white">
          <div>
            <div className="font-display font-black tracking-tighter text-xl">PARTNER DRILL-DOWN</div>
            {data && (
              <div className="font-mono text-xs text-neutral-500 truncate max-w-[60vw]">
                {data.partner?.url} · WINDOW {data.window_hours}h
              </div>
            )}
          </div>
          <button className="btn-flat-outline px-2 py-1 text-xs" data-testid="admin-drill-close" onClick={onClose}>
            <X size={14} weight="bold" />
          </button>
        </div>

        {err && <div className="p-4 text-red-600 font-mono text-sm">{err}</div>}
        {!data && !err && <div className="p-4 label-mono text-neutral-500">LOADING…</div>}

        {data && (
          <div className="p-4 space-y-5">
            <div>
              <div className="label-mono text-xs mb-2">PER-EVENT BREAKDOWN</div>
              {data.by_event.length === 0 && (
                <div className="label-mono text-xs text-neutral-500 px-3 py-3 border-2 border-black">
                  NO TRAFFIC IN WINDOW.
                </div>
              )}
              {data.by_event.length > 0 && (
                <div className="border-2 border-black">
                  <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
                    <div className="col-span-3">EVENT</div>
                    <div className="col-span-1 text-center">OK</div>
                    <div className="col-span-1 text-center">FAIL</div>
                    <div className="col-span-1 text-center">DEAD</div>
                    <div className="col-span-1 text-center">RETRY</div>
                    <div className="col-span-2 text-center">SUCCESS</div>
                    <div className="col-span-1 text-center">P50</div>
                    <div className="col-span-1 text-center">P95</div>
                    <div className="col-span-1 text-center">LAST FAIL</div>
                  </div>
                  {data.by_event.map((row) => (
                    <div
                      key={row.event}
                      data-testid={`admin-drill-event-${row.event}`}
                      className="grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 text-sm"
                    >
                      <div className="col-span-3 font-mono truncate" title={row.event}>{row.event}</div>
                      <div className="col-span-1 text-center font-mono">{row.delivered_count}</div>
                      <div className="col-span-1 text-center font-mono">{row.failed_count}</div>
                      <div className="col-span-1 text-center font-mono">{row.dead_count}</div>
                      <div className="col-span-1 text-center font-mono">{row.pending_retry_count}</div>
                      <div className="col-span-2 text-center font-mono">{fmtPct(row.success_rate)}</div>
                      <div className="col-span-1 text-center font-mono">{fmtMs(row.p50_latency_ms)}</div>
                      <div className="col-span-1 text-center font-mono">{fmtMs(row.p95_latency_ms)}</div>
                      <div className="col-span-1 text-center font-mono text-xs">
                        {row.last_failure_at ? new Date(row.last_failure_at).toISOString().slice(11, 16) : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="label-mono text-xs mb-2">
                RECENT FAILURES ({data.recent_failures?.length || 0})
              </div>
              {(!data.recent_failures || data.recent_failures.length === 0) && (
                <div className="label-mono text-xs text-neutral-500 px-3 py-3 border-2 border-black">
                  NO RECENT FAILURES IN WINDOW.
                </div>
              )}
              {data.recent_failures?.map((s) => (
                <div
                  key={s.id}
                  data-testid={`admin-drill-failure-${s.id}`}
                  className="border-2 border-black p-2 mb-2 text-xs font-mono space-y-1"
                >
                  <div>
                    <span className={(STATUS_CHIP[s.status] || STATUS_CHIP.failed).cls}>
                      {(STATUS_CHIP[s.status] || STATUS_CHIP.failed).label}
                    </span>{" "}
                    <span className="text-neutral-500">{s.event}</span>{" "}
                    <span className="text-neutral-400">HTTP {s.last_status ?? "—"}</span>{" "}
                    <span className="text-neutral-400">ATT {s.attempts}</span>
                  </div>
                  {s.last_error && (
                    <div className="text-red-600 break-all">{s.last_error}</div>
                  )}
                  <div className="text-neutral-400">{fmtIso(s.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============== ALERTS PANEL (Tier 2 — observer-only) ===============

// =============== MAINTENANCE WINDOWS (iter-15f UI) ===============

const SCOPE_TYPES = ["global", "partner", "event_type"];

function _localDateTimeToISO(local) {
  // <input type="datetime-local"> gives "YYYY-MM-DDTHH:MM" with NO timezone.
  // Interpret as local browser time and convert to ISO with offset.
  if (!local) return "";
  const d = new Date(local);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

function _nowLocalForInput() {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _windowStatus(w, now = new Date()) {
  const s = new Date(w.starts_at);
  const e = new Date(w.ends_at);
  if (now < s) return "scheduled";
  if (now >= e) return "expired";
  return "active";
}

function MaintenancePanel({ onAuthFail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await Admin.Alerts.listMaintenance();
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { onAuthFail(); return; }
      toast.error("MAINTENANCE LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const remove = async (w) => {
    if (!window.confirm(`Delete maintenance window "${w.name}"?`)) return;
    try {
      await Admin.Alerts.deleteMaintenance(w.id);
      toast.success("WINDOW DELETED");
      reload();
    } catch (e) {
      toast.error("DELETE FAILED");
    }
  };

  const grouped = useMemo(() => {
    const now = new Date();
    const active = [], scheduled = [], expired = [];
    for (const w of items) {
      const st = _windowStatus(w, now);
      if (st === "active") active.push(w);
      else if (st === "scheduled") scheduled.push(w);
      else expired.push(w);
    }
    return { active, scheduled, expired };
  }, [items]);

  return (
    <div className="space-y-4" data-testid="admin-maintenance-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-xs text-neutral-500">
          TRANSITION-LAYER SUPPRESSION FILTER · STATE FLIPS · OUTBOUND GATED
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNew(true)}
            className="btn-flat text-xs"
            data-testid="admin-maintenance-new-btn"
          >
            <Wrench size={14} weight="bold" /> NEW WINDOW
          </button>
          <button
            onClick={reload}
            className="btn-flat-outline text-xs"
            data-testid="admin-maintenance-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </div>
      </div>

      {showNew && (
        <NewMaintenanceForm
          onCancel={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}

      <MaintenanceSection
        title="ACTIVE NOW"
        items={grouped.active}
        emptyText="NO ACTIVE MAINTENANCE WINDOWS — ALERTS FLOW NORMALLY."
        chipClass="chip-live"
        chipLabel="ACTIVE"
        onRemove={remove}
        loading={loading}
        sectionId="active"
      />
      <MaintenanceSection
        title="SCHEDULED"
        items={grouped.scheduled}
        emptyText="NONE SCHEDULED."
        chipClass=""
        chipLabel="SCHEDULED"
        onRemove={remove}
        loading={false}
        sectionId="scheduled"
      />
      <MaintenanceSection
        title="EXPIRED (LAST 50)"
        items={grouped.expired.slice(0, 50)}
        emptyText="NO HISTORICAL WINDOWS."
        chipClass="bg-neutral-200"
        chipLabel="EXPIRED"
        onRemove={remove}
        loading={false}
        sectionId="expired"
      />
    </div>
  );
}

function MaintenanceSection({ title, items, emptyText, chipClass, chipLabel, onRemove, loading, sectionId }) {
  return (
    <div data-testid={`admin-maintenance-section-${sectionId}`}>
      <div className="label-mono text-xs mb-2">{title} ({items.length})</div>
      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-3">NAME</div>
          <div className="col-span-2">SCOPE</div>
          <div className="col-span-3">STARTS</div>
          <div className="col-span-3">ENDS</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {loading && (
          <div className="px-3 py-6 text-center label-mono text-neutral-500">LOADING…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-3 py-6 text-center label-mono text-xs text-neutral-500">
            {emptyText}
          </div>
        )}
        {items.map((w) => {
          const scope = w.scope || {};
          const scopeDesc =
            scope.type === "partner" ? `partner: ${(scope.webhook_id || "").slice(0, 8)}…`
            : scope.type === "event_type" ? `event: ${scope.event_type}`
            : "GLOBAL";
          return (
            <div
              key={w.id}
              data-testid={`admin-maintenance-row-${w.id}`}
              className="grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 text-sm"
            >
              <div className="col-span-3 font-mono truncate flex items-center gap-2" title={w.name}>
                <span className={`chip ${chipClass}`}>{chipLabel}</span>
                <span className="truncate">{w.name}</span>
              </div>
              <div className="col-span-2 font-mono text-xs truncate" title={scopeDesc}>
                {scopeDesc}
              </div>
              <div className="col-span-3 font-mono text-xs">{fmtIso(w.starts_at)}</div>
              <div className="col-span-3 font-mono text-xs">{fmtIso(w.ends_at)}</div>
              <div className="col-span-1 flex justify-end">
                <button
                  className="btn-flat-outline px-2 py-1 text-xs"
                  data-testid={`admin-maintenance-delete-${w.id}`}
                  onClick={() => onRemove(w)}
                  title="Delete window"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewMaintenanceForm({ onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState("global");
  const [webhookId, setWebhookId] = useState("");
  const [eventType, setEventType] = useState("");
  const [startsAt, setStartsAt] = useState(_nowLocalForInput());
  const [endsAt, setEndsAt] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 60, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("NAME REQUIRED");
      return;
    }
    const startISO = _localDateTimeToISO(startsAt);
    const endISO = _localDateTimeToISO(endsAt);
    if (!startISO || !endISO) {
      toast.error("INVALID TIMESTAMPS");
      return;
    }
    if (new Date(endISO) <= new Date(startISO)) {
      toast.error("ENDS_AT MUST BE AFTER STARTS_AT");
      return;
    }
    const scope = { type: scopeType };
    if (scopeType === "partner") {
      if (!webhookId.trim()) {
        toast.error("WEBHOOK_ID REQUIRED FOR PARTNER SCOPE");
        return;
      }
      scope.webhook_id = webhookId.trim();
    }
    if (scopeType === "event_type") {
      if (!eventType.trim()) {
        toast.error("EVENT_TYPE REQUIRED FOR EVENT-TYPE SCOPE");
        return;
      }
      scope.event_type = eventType.trim();
    }
    setSubmitting(true);
    try {
      await Admin.Alerts.createMaintenance({
        name: name.trim(),
        scope,
        starts_at: startISO,
        ends_at: endISO,
      });
      toast.success("WINDOW CREATED");
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || "CREATE FAILED";
      toast.error(msg.toUpperCase());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border-2 border-black p-4 space-y-3 bg-white"
      data-testid="admin-maintenance-new-form"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black tracking-tighter text-lg">NEW MAINTENANCE WINDOW</h2>
        <button
          className="btn-flat-outline px-2 py-1"
          onClick={onCancel}
          data-testid="admin-maintenance-new-cancel"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-mono text-[10px] text-neutral-500">NAME</label>
          <input
            data-testid="admin-maintenance-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q1 deploy window"
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm"
          />
        </div>
        <div>
          <label className="label-mono text-[10px] text-neutral-500">SCOPE</label>
          <select
            data-testid="admin-maintenance-scope-select"
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value)}
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm bg-white"
          >
            {SCOPE_TYPES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {scopeType === "partner" && (
        <div data-testid="admin-maintenance-partner-fields">
          <label className="label-mono text-[10px] text-neutral-500">WEBHOOK_ID</label>
          <input
            data-testid="admin-maintenance-webhook-id-input"
            value={webhookId}
            onChange={(e) => setWebhookId(e.target.value)}
            placeholder="UUID of the partner webhook"
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm"
          />
        </div>
      )}

      {scopeType === "event_type" && (
        <div data-testid="admin-maintenance-event-fields">
          <label className="label-mono text-[10px] text-neutral-500">EVENT_TYPE</label>
          <input
            data-testid="admin-maintenance-event-type-input"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            placeholder="e.g. photo.uploaded"
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-mono text-[10px] text-neutral-500">STARTS AT (LOCAL)</label>
          <input
            type="datetime-local"
            data-testid="admin-maintenance-starts-input"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm"
          />
        </div>
        <div>
          <label className="label-mono text-[10px] text-neutral-500">ENDS AT (LOCAL)</label>
          <input
            type="datetime-local"
            data-testid="admin-maintenance-ends-input"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="w-full border-2 border-black px-2 py-1 font-mono text-sm"
          />
        </div>
      </div>

      <div className="label-mono text-[10px] text-neutral-500">
        BOTH BOUNDS REQUIRED · ENDS_AT &gt; STARTS_AT · NO OPEN-ENDED WINDOWS
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className="btn-flat-outline text-xs"
          data-testid="admin-maintenance-new-cancel-2"
        >
          CANCEL
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          className="btn-flat text-xs disabled:opacity-50"
          data-testid="admin-maintenance-submit"
        >
          {submitting ? "CREATING…" : "CREATE WINDOW"}
        </button>
      </div>
    </div>
  );
}


function AlertsPanel({ onAuthFail }) {
  const [rules, setRules] = useState([]);
  const [state, setState] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [explainRule, setExplainRule] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, s, d] = await Promise.all([
        Admin.Alerts.listRules(),
        Admin.Alerts.listState(),
        Admin.Alerts.listDeliveries({ limit: 30 }),
      ]);
      setRules(r.items || []);
      setKinds(r.rule_kinds || []);
      setState(s.items || []);
      setDeliveries(d.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { onAuthFail(); return; }
      toast.error("ALERTS LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const stateByRule = useMemo(() => {
    const m = {};
    for (const s of state) m[s.rule_id] = s;
    return m;
  }, [state]);

  const evaluate = async () => {
    try {
      const r = await Admin.Alerts.evaluate();
      toast.success(`EVALUATED ${r.evaluated} · ${r.transitions.length} TRANSITIONS`);
      reload();
    } catch (e) {
      toast.error("EVALUATE FAILED");
    }
  };

  const toggle = async (rule) => {
    try {
      await Admin.Alerts.patchRule(rule.id, { enabled: !rule.enabled });
      reload();
    } catch (e) { toast.error("PATCH FAILED"); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await Admin.Alerts.deleteRule(rule.id);
      toast.success("DELETED");
      reload();
    } catch (e) { toast.error("DELETE FAILED"); }
  };

  const fireTest = async (rule) => {
    try {
      await Admin.Alerts.fireTest(rule.id);
      toast.success("TEST FIRED");
      setTimeout(reload, 1500);
    } catch (e) { toast.error("FIRE-TEST FAILED"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-alerts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-xs text-neutral-500">
          OBSERVER LAYER · READS HISTORY · WRITES ONLY ALERT_* COLLECTIONS
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNew(true)}
            className="btn-flat text-xs"
            data-testid="admin-alerts-new-btn"
          >
            <Bell size={14} weight="bold" /> NEW RULE
          </button>
          <button
            onClick={evaluate}
            className="btn-flat-outline text-xs"
            data-testid="admin-alerts-evaluate-btn"
            title="Force an evaluation pass now (background loop runs every 60s)"
          >
            <Lightning size={14} weight="bold" /> EVALUATE NOW
          </button>
          <button onClick={reload} className="btn-flat-outline text-xs" data-testid="admin-alerts-refresh">
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </div>
      </div>

      {showNew && (
        <NewRuleForm
          kinds={kinds}
          onCancel={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}

      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-3">RULE</div>
          <div className="col-span-2">KIND</div>
          <div className="col-span-3">TARGET</div>
          <div className="col-span-1 text-center">STATE</div>
          <div className="col-span-1 text-center">ENABLED</div>
          <div className="col-span-2 text-right">ACTIONS</div>
        </div>
        {loading && <div className="px-3 py-6 text-center label-mono text-neutral-500">LOADING…</div>}
        {!loading && rules.length === 0 && (
          <div className="px-3 py-8 text-center label-mono text-neutral-500" data-testid="admin-alerts-empty">
            NO ALERT RULES. CLICK "NEW RULE" TO START OBSERVING.
          </div>
        )}
        {rules.map((rule) => {
          const s = stateByRule[rule.id];
          const firing = s?.state === "firing";
          return (
            <RuleRow
              key={rule.id}
              rule={rule}
              s={s}
              firing={firing}
              onToggle={() => toggle(rule)}
              onFireTest={() => fireTest(rule)}
              onExplain={() => setExplainRule(rule)}
              onDelete={() => remove(rule)}
            />
          );
        })}
      </div>

      <div>
        <div className="label-mono text-xs mb-2">RECENT ALERT DELIVERIES ({deliveries.length})</div>
        {deliveries.length === 0 && (
          <div className="label-mono text-xs text-neutral-500 px-3 py-3 border-2 border-black">
            NO OUTBOUND ALERTS YET.
          </div>
        )}
        {deliveries.length > 0 && (
          <div className="border-2 border-black">
            <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
              <div className="col-span-2">STATUS</div>
              <div className="col-span-3">EVENT</div>
              <div className="col-span-4">URL</div>
              <div className="col-span-1 text-center">ATT</div>
              <div className="col-span-2">WHEN</div>
            </div>
            {deliveries.map((d) => (
              <div
                key={d.id}
                data-testid={`admin-alert-delivery-${d.id}`}
                className="grid grid-cols-12 items-center px-3 py-2 border-b border-neutral-300 text-sm"
              >
                <div className="col-span-2">
                  <span className={(STATUS_CHIP[d.status] || STATUS_CHIP.pending).cls}>
                    {(STATUS_CHIP[d.status] || STATUS_CHIP.pending).label}
                  </span>
                </div>
                <div className="col-span-3 font-mono text-xs truncate">{d.event}</div>
                <div className="col-span-4 font-mono text-xs truncate" title={d.target_url}>
                  {d.target_url}
                </div>
                <div className="col-span-1 text-center font-mono">{d.attempts}</div>
                <div className="col-span-2 font-mono text-xs">{fmtIso(d.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {explainRule && (
        <ExplainAlertModal
          rule={explainRule}
          state={stateByRule[explainRule.id]}
          onClose={() => setExplainRule(null)}
          onAuthFail={onAuthFail}
        />
      )}
    </div>
  );
}


function RuleRow({ rule, s, firing, onToggle, onFireTest, onExplain, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const hasEvidence = !!s?.last_evidence;
  const fmt = rule.target?.format || "generic";
  const suppression = s?.last_suppression_reason;
  return (
    <div
      data-testid={`admin-alert-rule-${rule.id}`}
      className={`border-b border-neutral-300 ${firing ? "bg-red-50" : ""}`}
    >
      <div className="grid grid-cols-12 items-center px-3 py-2 text-sm">
        <div className="col-span-3 font-mono truncate flex items-center gap-1" title={rule.name}>
          {hasEvidence && (
            <button
              data-testid={`admin-alert-evidence-toggle-${rule.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="text-neutral-400 hover:text-black"
              title="Show last evidence snapshot"
            >
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <span className="truncate">{rule.name}</span>
        </div>
        <div className="col-span-2 font-mono text-xs text-neutral-500 truncate">
          {rule.kind}
          {fmt !== "generic" && (
            <span className="ml-1 chip">{fmt.toUpperCase()}</span>
          )}
        </div>
        <div className="col-span-3 font-mono text-xs truncate" title={rule.target?.url}>
          {rule.target?.url}
        </div>
        <div className="col-span-1 text-center">
          {firing ? (
            <span className="chip chip-live inline-flex items-center gap-1">
              <WarningOctagon size={10} weight="bold" /> FIRE
            </span>
          ) : s ? (
            <span className="chip inline-flex items-center gap-1">
              <CheckCircle size={10} weight="bold" /> CLEAR
            </span>
          ) : (
            <span className="chip">—</span>
          )}
          {suppression && (
            <div
              className="label-mono text-[9px] text-orange-700 mt-0.5"
              data-testid={`admin-alert-suppressed-${rule.id}`}
              title={suppression}
            >
              SUPPRESSED
            </div>
          )}
        </div>
        <div className="col-span-1 text-center">
          <button
            className={`chip cursor-pointer ${rule.enabled ? "bg-black text-white" : ""}`}
            data-testid={`admin-alert-toggle-${rule.id}`}
            onClick={onToggle}
            title="Toggle enabled"
          >
            {rule.enabled ? "ON" : "OFF"}
          </button>
        </div>
        <div className="col-span-2 flex justify-end gap-1">
          <button
            className="btn-flat-outline px-2 py-1 text-xs"
            data-testid={`admin-alert-explain-${rule.id}`}
            onClick={onExplain}
            title="Explain this alert — show decision trace history"
          >
            <ClockClockwise size={12} weight="bold" />
          </button>
          <button
            className="btn-flat-outline px-2 py-1 text-xs"
            data-testid={`admin-alert-firetest-${rule.id}`}
            onClick={onFireTest}
            title="Send synthetic alert (does not change state)"
          >
            <Lightning size={12} weight="bold" />
          </button>
          <button
            className="btn-flat-outline px-2 py-1 text-xs"
            data-testid={`admin-alert-delete-${rule.id}`}
            onClick={onDelete}
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>
      {expanded && hasEvidence && (
        <div
          className="px-3 pb-3 -mt-1"
          data-testid={`admin-alert-evidence-${rule.id}`}
        >
          <div className="label-mono text-xs text-neutral-500 mb-1">
            LAST EVIDENCE (snapshot from {fmtIso(s.last_evaluated_at)})
            {suppression && (
              <span className="ml-2 text-orange-700">· {suppression}</span>
            )}
          </div>
          <pre className="border-2 border-black p-2 bg-neutral-50 text-xs overflow-auto max-h-48 whitespace-pre-wrap">{JSON.stringify(s.last_evidence, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}


function ExplainAlertModal({ rule, state, onClose, onAuthFail }) {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [decisionFilter, setDecisionFilter] = useState(""); // "", "emit", "suppressed"
  const [reasonFilter, setReasonFilter] = useState(""); // "", "transition_emit", "suppressed_by_dedup", ...
  const [expandedId, setExpandedId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await Admin.Alerts.listTraces({
        rule_id: rule.id,
        decision: decisionFilter || undefined,
        reason: reasonFilter || undefined,
        limit: 100,
      });
      setTraces(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { onAuthFail?.(); onClose(); return; }
      toast.error("TRACES LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [decisionFilter, reasonFilter]);

  const counts = useMemo(() => {
    const c = { emit: 0, suppressed: 0 };
    for (const t of traces) {
      if (t.decision === "emit") c.emit += 1;
      else if (t.decision === "suppressed") c.suppressed += 1;
    }
    return c;
  }, [traces]);

  // Build the "decision pipeline" labels from the reason field —
  // surfaces the gate-by-gate path without re-running anything.
  const pipelineFor = (t) => {
    const gates = [];
    gates.push({ name: "state_change", outcome: "transition", on: true });
    if (t.reason === "suppressed_by_maintenance") {
      gates.push({ name: "maintenance", outcome: "MATCHED · suppressed", on: false });
    } else if (t.reason === "suppressed_by_dedup") {
      gates.push({ name: "maintenance", outcome: "miss · passthrough", on: true });
      gates.push({ name: "dedup", outcome: "MATCHED · suppressed", on: false });
    } else if (t.reason === "transition_emit") {
      gates.push({ name: "maintenance", outcome: "miss · passthrough", on: true });
      gates.push({ name: "dedup", outcome: "miss · passthrough", on: true });
      gates.push({ name: "emit", outcome: "outbound enqueued", on: true });
    } else {
      gates.push({ name: t.reason, outcome: t.reason, on: t.decision === "emit" });
    }
    return gates;
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-alert-explain-modal"
    >
      <div
        className="bg-white border-2 border-black w-full max-w-4xl mt-8 mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black bg-neutral-50">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <ClockClockwise size={18} weight="bold" />
              <span className="font-display font-black tracking-tighter text-lg">EXPLAIN ALERT</span>
            </div>
            <div className="label-mono text-xs text-neutral-600">
              {rule.name} · <span className="font-mono">{rule.kind}</span>
              {state?.state && (
                <span className={`ml-2 chip ${state.state === "firing" ? "chip-live" : ""}`}>
                  {state.state === "firing" ? "FIRING" : "CLEAR"}
                </span>
              )}
            </div>
          </div>
          <button
            className="btn-flat-outline px-2 py-1"
            data-testid="admin-alert-explain-close"
            onClick={onClose}
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Filters + counts */}
        <div className="px-4 py-3 border-b border-neutral-300 flex items-center gap-2 flex-wrap label-mono text-xs">
          <span className="text-neutral-500">DECISION:</span>
          {["", "emit", "suppressed"].map((d) => (
            <button
              key={d || "all"}
              data-testid={`admin-alert-explain-filter-${d || "all"}`}
              onClick={() => setDecisionFilter(d)}
              className={`chip cursor-pointer ${decisionFilter === d ? "bg-black text-white" : ""}`}
            >
              {d ? d.toUpperCase() : "ALL"}
            </button>
          ))}
          <span className="ml-4 text-neutral-500">REASON:</span>
          <select
            data-testid="admin-alert-explain-reason"
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            className="border-2 border-black bg-white px-2 py-1 text-xs font-mono"
          >
            <option value="">all</option>
            <option value="transition_emit">transition_emit</option>
            <option value="suppressed_by_dedup">suppressed_by_dedup</option>
            <option value="suppressed_by_maintenance">suppressed_by_maintenance</option>
          </select>
          <span className="ml-auto text-neutral-500">
            {counts.emit} EMIT · {counts.suppressed} SUPPRESSED
          </span>
          <button
            data-testid="admin-alert-explain-refresh"
            onClick={reload}
            className="btn-flat-outline px-2 py-1 text-xs"
            title="Reload traces"
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>

        {/* Timeline */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center label-mono text-neutral-500">LOADING…</div>
          )}
          {!loading && traces.length === 0 && (
            <div
              data-testid="admin-alert-explain-empty"
              className="px-4 py-8 text-center label-mono text-xs text-neutral-500 space-y-1"
            >
              <div>NO STATE-CHANGING DECISIONS YET FOR THIS RULE.</div>
              <div className="text-neutral-400">
                Steady-state evaluations (no_transition) and initial baselines
                <br />are intentionally NOT traced — only emits and suppressions are.
              </div>
            </div>
          )}
          {!loading && traces.map((t) => {
            const isOpen = expandedId === t.id;
            const cls =
              t.decision === "emit"
                ? "border-l-4 border-l-black bg-white"
                : "border-l-4 border-l-orange-500 bg-orange-50";
            return (
              <div
                key={t.id}
                data-testid={`admin-alert-explain-trace-${t.id}`}
                className={`px-4 py-3 border-b border-neutral-300 ${cls}`}
              >
                <button
                  className="w-full text-left flex items-center gap-3 flex-wrap"
                  onClick={() => setExpandedId(isOpen ? null : t.id)}
                >
                  <span className="font-mono text-xs text-neutral-600">
                    {fmtIso(t.created_at)}
                  </span>
                  <span
                    className={`chip ${t.decision === "emit" ? "bg-black text-white" : "bg-orange-200 text-orange-900"}`}
                  >
                    {t.decision.toUpperCase()}
                  </span>
                  <span className="font-mono text-xs">
                    {t.prev_state || "—"} → <strong>{t.new_state}</strong>
                  </span>
                  <span className="font-mono text-xs text-neutral-700">{t.reason}</span>
                  {t.delivery_id && (
                    <span
                      className="font-mono text-xs text-neutral-500"
                      title="Outbound alert_deliveries id"
                    >
                      dlv:{t.delivery_id.slice(0, 8)}
                    </span>
                  )}
                  <span className="ml-auto text-neutral-400 text-xs">
                    {isOpen ? "▼" : "▶"}
                  </span>
                </button>
                {isOpen && (
                  <div className="mt-3 space-y-2 pl-1">
                    {/* Decision pipeline */}
                    <div>
                      <div className="label-mono text-[10px] text-neutral-500 mb-1">
                        DECISION PIPELINE (inferred from reason)
                      </div>
                      <div className="flex items-center gap-1 flex-wrap text-xs font-mono">
                        {pipelineFor(t).map((g, i) => (
                          <span key={g.name} className="flex items-center gap-1">
                            <span
                              className={`chip ${g.on ? "" : "bg-orange-200 text-orange-900"}`}
                              data-testid={`admin-alert-explain-gate-${i}`}
                            >
                              {g.name}: {g.outcome}
                            </span>
                            {i < pipelineFor(t).length - 1 && (
                              <span className="text-neutral-400">→</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Suppression detail */}
                    {t.suppression_reason && (
                      <div>
                        <div className="label-mono text-[10px] text-neutral-500 mb-1">
                          SUPPRESSION REASON
                        </div>
                        <div className="font-mono text-xs text-orange-800 break-all">
                          {t.suppression_reason}
                        </div>
                      </div>
                    )}
                    {/* Evidence */}
                    <div>
                      <div className="label-mono text-[10px] text-neutral-500 mb-1">
                        EVIDENCE (snapshot the evaluator returned)
                      </div>
                      <pre
                        data-testid={`admin-alert-explain-evidence-${t.id}`}
                        className="border-2 border-black p-2 bg-neutral-50 text-[11px] overflow-auto max-h-40 whitespace-pre-wrap"
                      >
                        {t.evidence ? JSON.stringify(t.evidence, null, 2) : "(none — clear transition)"}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t-2 border-black bg-neutral-50 label-mono text-[10px] text-neutral-500">
          DECISION TRACE LOG · INITIAL CLEARS + NO-TRANSITION EVALS ARE NOT PERSISTED · 30-DAY RETENTION
        </div>
      </div>
    </div>
  );
}


function NewRuleForm({ kinds, onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState(kinds[0] || "dead_delivery_age");
  const [url, setUrl] = useState("https://httpbin.org/post");
  const [format, setFormat] = useState("generic");
  const [pdRoutingKey, setPdRoutingKey] = useState("");
  // Transition-layer (applies to ALL kinds).
  const [dedupMinutes, setDedupMinutes] = useState(0);
  // dead_delivery_age
  const [ageMinutes, setAgeMinutes] = useState(60);
  // partner_degraded
  const [windowMinutes, setWindowMinutes] = useState(60);
  const [threshold, setThreshold] = useState(0.9);
  const [minTotal, setMinTotal] = useState(5);
  const [pdWebhookId, setPdWebhookId] = useState("");
  // event_silence
  const [eventType, setEventType] = useState("photo.uploaded");
  const [silenceWindow, setSilenceWindow] = useState(30);
  const [requirePrior, setRequirePrior] = useState(true);
  // dead_delivery_count_spike
  const [spikeWindow, setSpikeWindow] = useState(15);
  const [baselineWindow, setBaselineWindow] = useState(360);
  const [minCount, setMinCount] = useState(5);
  const [multiplier, setMultiplier] = useState(3.0);
  // webhook_silence_per_partner
  const [silenceWebhookId, setSilenceWebhookId] = useState("");
  const [partnerSilenceWindow, setPartnerSilenceWindow] = useState(30);
  // attempts_burst_per_event_type
  const [burstEventType, setBurstEventType] = useState("photo.uploaded");
  const [burstWindow, setBurstWindow] = useState(10);
  const [attemptsThreshold, setAttemptsThreshold] = useState(25);

  const [submitting, setSubmitting] = useState(false);

  const buildParams = () => {
    const base = dedupMinutes > 0 ? { dedup_window_minutes: Number(dedupMinutes) } : {};
    if (kind === "dead_delivery_age")
      return { ...base, age_minutes: Number(ageMinutes) };
    if (kind === "partner_degraded")
      return {
        ...base,
        window_minutes: Number(windowMinutes),
        success_rate_threshold: Number(threshold),
        min_total: Number(minTotal),
        ...(pdWebhookId.trim() ? { webhook_id: pdWebhookId.trim() } : {}),
      };
    if (kind === "event_silence")
      return {
        ...base,
        event_type: eventType.trim(),
        silence_window_minutes: Number(silenceWindow),
        require_prior: !!requirePrior,
      };
    if (kind === "dead_delivery_count_spike")
      return {
        ...base,
        spike_window_minutes: Number(spikeWindow),
        baseline_window_minutes: Number(baselineWindow),
        min_count: Number(minCount),
        multiplier: Number(multiplier),
      };
    if (kind === "webhook_silence_per_partner")
      return {
        ...base,
        webhook_id: silenceWebhookId.trim(),
        silence_window_minutes: Number(partnerSilenceWindow),
        require_prior: !!requirePrior,
      };
    if (kind === "attempts_burst_per_event_type")
      return {
        ...base,
        event_type: burstEventType.trim(),
        burst_window_minutes: Number(burstWindow),
        attempts_threshold: Number(attemptsThreshold),
      };
    return base;
  };

  const submit = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error("NAME + URL REQUIRED");
      return;
    }
    if (format === "pagerduty" && !pdRoutingKey.trim()) {
      toast.error("PAGERDUTY ROUTING KEY REQUIRED");
      return;
    }
    if (kind === "webhook_silence_per_partner" && !silenceWebhookId.trim()) {
      toast.error("WEBHOOK_ID REQUIRED FOR webhook_silence_per_partner");
      return;
    }
    setSubmitting(true);
    try {
      const target = { url: url.trim(), format };
      if (format === "pagerduty") target.pagerduty_routing_key = pdRoutingKey.trim();
      await Admin.Alerts.createRule({
        name: name.trim(), kind, params: buildParams(),
        target, enabled: true,
      });
      toast.success("RULE CREATED");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CREATE FAILED");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-2 border-black p-4 space-y-3 bg-neutral-50" data-testid="admin-alerts-new-form">
      <div className="font-display font-black tracking-tighter text-xl">NEW ALERT RULE</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="space-y-1">
          <div className="label-mono text-xs">NAME</div>
          <input
            data-testid="admin-alerts-form-name"
            className="w-full border-2 border-black px-2 py-1 font-mono"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dead deliveries older than 1h"
          />
        </label>
        <label className="space-y-1">
          <div className="label-mono text-xs">KIND</div>
          <select
            data-testid="admin-alerts-form-kind"
            className="w-full border-2 border-black px-2 py-1 font-mono"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="space-y-1 col-span-2">
          <div className="label-mono text-xs">NOTIFICATION URL</div>
          <input
            data-testid="admin-alerts-form-url"
            className="w-full border-2 border-black px-2 py-1 font-mono"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your.endpoint/alerts"
          />
        </label>
        <label className="space-y-1">
          <div className="label-mono text-xs">TARGET FORMAT</div>
          <select
            data-testid="admin-alerts-form-format"
            className="w-full border-2 border-black px-2 py-1 font-mono"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            <option value="generic">generic (JSON + HMAC)</option>
            <option value="slack">slack (Incoming Webhook)</option>
            <option value="pagerduty">pagerduty (Events API v2)</option>
          </select>
        </label>
        {format === "pagerduty" && (
          <label className="space-y-1">
            <div className="label-mono text-xs">PAGERDUTY ROUTING KEY</div>
            <input
              data-testid="admin-alerts-form-pdkey"
              type="password"
              className="w-full border-2 border-black px-2 py-1 font-mono"
              value={pdRoutingKey}
              onChange={(e) => setPdRoutingKey(e.target.value)}
            />
          </label>
        )}
        <label className="space-y-1 col-span-2 border-t-2 border-dashed border-neutral-300 pt-3">
          <div className="label-mono text-xs">
            DEDUP WINDOW (MIN) <span className="text-neutral-400">— transition-layer gate; 0 = no dedup</span>
          </div>
          <input
            data-testid="admin-alerts-form-dedup"
            type="number" min={0}
            className="w-full border-2 border-black px-2 py-1 font-mono"
            value={dedupMinutes}
            onChange={(e) => setDedupMinutes(e.target.value)}
          />
        </label>
        {kind === "dead_delivery_age" && (
          <label className="space-y-1">
            <div className="label-mono text-xs">AGE (MINUTES)</div>
            <input
              data-testid="admin-alerts-form-age"
              type="number" min={1}
              className="w-full border-2 border-black px-2 py-1 font-mono"
              value={ageMinutes}
              onChange={(e) => setAgeMinutes(e.target.value)}
            />
          </label>
        )}
        {kind === "partner_degraded" && (
          <>
            <label className="space-y-1">
              <div className="label-mono text-xs">WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={windowMinutes} onChange={(e) => setWindowMinutes(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">THRESHOLD (0..1)</div>
              <input type="number" step="0.01" min={0} max={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={threshold} onChange={(e) => setThreshold(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">MIN TOTAL</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={minTotal} onChange={(e) => setMinTotal(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">WEBHOOK_ID (OPTIONAL · scope to one partner)</div>
              <input className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={pdWebhookId} onChange={(e) => setPdWebhookId(e.target.value)}
                     placeholder="leave blank for all partners" />
            </label>
          </>
        )}
        {kind === "event_silence" && (
          <>
            <label className="space-y-1">
              <div className="label-mono text-xs">EVENT TYPE</div>
              <input data-testid="admin-alerts-form-event-type"
                     className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={eventType} onChange={(e) => setEventType(e.target.value)}
                     placeholder="e.g. photo.uploaded" />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">SILENCE WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={silenceWindow} onChange={(e) => setSilenceWindow(e.target.value)} />
            </label>
            <label className="flex items-center gap-2 col-span-2 label-mono text-xs">
              <input type="checkbox" checked={requirePrior}
                     onChange={(e) => setRequirePrior(e.target.checked)} />
              REQUIRE PRIOR EVENT (don't fire on fresh install)
            </label>
          </>
        )}
        {kind === "dead_delivery_count_spike" && (
          <>
            <label className="space-y-1">
              <div className="label-mono text-xs">SPIKE WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={spikeWindow} onChange={(e) => setSpikeWindow(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">BASELINE WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={baselineWindow} onChange={(e) => setBaselineWindow(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">MIN COUNT</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={minCount} onChange={(e) => setMinCount(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">MULTIPLIER (×baseline)</div>
              <input type="number" step="0.5" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
            </label>
          </>
        )}
        {kind === "webhook_silence_per_partner" && (
          <>
            <label className="space-y-1 col-span-2">
              <div className="label-mono text-xs">WEBHOOK_ID (REQUIRED · the partner to watch)</div>
              <input
                data-testid="admin-alerts-form-silence-webhook"
                className="w-full border-2 border-black px-2 py-1 font-mono"
                value={silenceWebhookId} onChange={(e) => setSilenceWebhookId(e.target.value)}
                placeholder="copy from Partner SLO drill-down" />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">SILENCE WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={partnerSilenceWindow} onChange={(e) => setPartnerSilenceWindow(e.target.value)} />
            </label>
            <label className="flex items-center gap-2 col-span-2 label-mono text-xs">
              <input type="checkbox" checked={requirePrior}
                     onChange={(e) => setRequirePrior(e.target.checked)} />
              REQUIRE PRIOR DELIVERY (don't fire on partners that haven't been used yet)
            </label>
          </>
        )}
        {kind === "attempts_burst_per_event_type" && (
          <>
            <label className="space-y-1">
              <div className="label-mono text-xs">EVENT TYPE</div>
              <input data-testid="admin-alerts-form-burst-event-type"
                     className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={burstEventType} onChange={(e) => setBurstEventType(e.target.value)} />
            </label>
            <label className="space-y-1">
              <div className="label-mono text-xs">BURST WINDOW (MIN)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={burstWindow} onChange={(e) => setBurstWindow(e.target.value)} />
            </label>
            <label className="space-y-1 col-span-2">
              <div className="label-mono text-xs">ATTEMPTS THRESHOLD (total in window)</div>
              <input type="number" min={1} className="w-full border-2 border-black px-2 py-1 font-mono"
                     value={attemptsThreshold} onChange={(e) => setAttemptsThreshold(e.target.value)} />
            </label>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button className="btn-flat-outline text-xs" onClick={onCancel} data-testid="admin-alerts-form-cancel">
          CANCEL
        </button>
        <button
          className="btn-flat text-xs"
          onClick={submit}
          disabled={submitting}
          data-testid="admin-alerts-form-submit"
        >
          {submitting ? "CREATING…" : "CREATE"}
        </button>
      </div>
    </div>
  );
}

// =============== AUDIT LOG (iter-34) ===============

function AuditLogPanel({ onAuthFail }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionPrefix, setActionPrefix] = useState("");
  const [actorKind, setActorKind] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await AdminAudit.list({
        limit: 200,
        action_prefix: actionPrefix || undefined,
        actor_kind: actorKind || undefined,
        outcome: outcome || undefined,
      });
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("UNAUTHORIZED"); onAuthFail(); return; }
      toast.error("LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionPrefix, actorKind, outcome]);

  return (
    <div className="space-y-4" data-testid="admin-audit-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-xs text-neutral-500">
          {items.length} / {total} ROWS · TTL 180 DAYS · NEVER LOGS SECRET VALUES
        </div>
        <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-audit-refresh">
          <ArrowsClockwise size={14} weight="bold" /> REFRESH
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="admin-audit-filters">
        <label className="space-y-1">
          <div className="label-mono text-[10px] text-neutral-500">ACTION PREFIX</div>
          <input
            data-testid="admin-audit-filter-action"
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
            placeholder="e.g. billing. / alerts. / auth."
            className="w-full border-2 border-black px-2 py-1 font-mono text-xs"
          />
        </label>
        <label className="space-y-1">
          <div className="label-mono text-[10px] text-neutral-500">ACTOR KIND</div>
          <select
            data-testid="admin-audit-filter-actor"
            value={actorKind}
            onChange={(e) => setActorKind(e.target.value)}
            className="w-full border-2 border-black bg-white px-2 py-1 font-mono text-xs"
          >
            <option value="">any</option>
            <option value="user">user (signed-in)</option>
            <option value="admin_token">admin_token</option>
            <option value="anon">anon</option>
            <option value="system">system</option>
          </select>
        </label>
        <label className="space-y-1">
          <div className="label-mono text-[10px] text-neutral-500">OUTCOME</div>
          <select
            data-testid="admin-audit-filter-outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="w-full border-2 border-black bg-white px-2 py-1 font-mono text-xs"
          >
            <option value="">any</option>
            <option value="ok">ok</option>
            <option value="fail">fail</option>
            <option value="denied">denied</option>
          </select>
        </label>
      </div>

      <div className="border-2 border-black">
        <div className="grid grid-cols-12 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-3">WHEN</div>
          <div className="col-span-3">ACTION</div>
          <div className="col-span-2">ACTOR</div>
          <div className="col-span-3">TARGET</div>
          <div className="col-span-1 text-center">OK</div>
        </div>
        {loading && <div className="px-3 py-6 label-mono text-neutral-500 text-center">LOADING…</div>}
        {!loading && items.length === 0 && (
          <div className="px-3 py-8 label-mono text-neutral-500 text-center" data-testid="admin-audit-empty">
            NO AUDIT ROWS MATCH FILTER.
          </div>
        )}
        {items.map((row) => (
          <div
            key={row.id}
            data-testid={`admin-audit-row-${row.id}`}
            className="grid grid-cols-12 items-start px-3 py-2 border-b border-neutral-300 text-sm"
          >
            <div className="col-span-3 font-mono text-xs">{fmtIso(row.ts)}</div>
            <div className="col-span-3 font-mono text-xs truncate" title={row.action}>{row.action}</div>
            <div className="col-span-2 font-mono text-xs truncate" title={row.actor?.id}>
              <span className="chip">{row.actor?.kind || "—"}</span>
              <span className="ml-1 text-neutral-500">{row.actor?.email || row.actor?.id}</span>
            </div>
            <div className="col-span-3 font-mono text-xs truncate" title={row.target || ""}>{row.target || "—"}</div>
            <div className="col-span-1 text-center">
              <span className={`chip ${row.outcome === "ok" ? "" : "chip-live"}`}>
                {row.outcome?.toUpperCase() || "—"}
              </span>
            </div>
            {row.meta && Object.keys(row.meta).length > 0 && (
              <div className="col-span-12 mt-1 label-mono text-[10px] text-neutral-500 border-l-2 border-neutral-300 pl-2 truncate">
                {Object.entries(row.meta).map(([k, v]) => `${k}=${v}`).join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== DEEP HEALTH (iter-34) ===============

function HealthPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await HealthDeep.fetch();
      setData(r);
    } catch (e) {
      setErr(e?.message || "Health check failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const overall = data?.status || (loading ? "checking" : "unknown");
  const overallCls =
    overall === "healthy"
      ? "bg-green-50 border-green-600"
      : overall === "degraded"
      ? "bg-red-50 border-red-600"
      : "bg-neutral-50";

  return (
    <div className="space-y-4" data-testid="admin-health-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-xs text-neutral-500">
          MONGO PING · SCHEDULER · ALERT EVALUATOR · CHECKIN LOOP
        </div>
        <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-health-refresh" disabled={loading}>
          <ArrowsClockwise size={14} weight="bold" /> {loading ? "CHECKING…" : "REFRESH"}
        </button>
      </div>

      <div
        className={`border-2 border-black p-4 ${overallCls}`}
        data-testid="admin-health-overall"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono text-xs text-neutral-500">OVERALL</div>
            <div className="font-display font-black text-3xl tracking-tighter">
              {overall.toUpperCase()}
            </div>
          </div>
          <Heartbeat
            size={40}
            weight="bold"
            className={overall === "healthy" ? "text-green-700" : "text-black"}
          />
        </div>
        {data?.checked_at && (
          <div className="label-mono text-xs text-neutral-500 mt-2">
            CHECKED {fmtIso(data.checked_at)}
          </div>
        )}
      </div>

      {err && (
        <div className="border-2 border-red-600 bg-red-50 p-3 font-mono text-sm" data-testid="admin-health-error">
          {err}
        </div>
      )}

      {data?.checks && (
        <div className="grid grid-cols-2 gap-2" data-testid="admin-health-checks">
          {Object.entries(data.checks).map(([name, c]) => (
            <div
              key={name}
              data-testid={`admin-health-check-${name}`}
              className={`border-2 border-black p-3 ${c.ok ? "bg-white" : "bg-red-50"}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 border-2 border-black shrink-0"
                  style={{ background: c.ok ? "#00AA5A" : "#FF2A2A" }}
                />
                <span className="font-mono text-sm">{name}</span>
              </div>
              <div className="font-mono text-xs text-neutral-600 mt-1 break-all">{c.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
